import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChildren,
} from "@angular/core";
import { formatLocalizedDateTime } from "../../utils/localized-date.util";
import { ExperimentListComponent } from "../experiment-list/experiment-list.component";
import { CatExpertRouterLink } from "../../app-routing-enum";
import { StateService } from "../../services/state.service";

@Component({
  selector: "app-row-group-experiment-list",
  templateUrl: "./row-group-experiment-list.component.html",
  styleUrl: "./row-group-experiment-list.component.scss",
})
export class RowGroupExperimentListComponent implements OnChanges, OnDestroy {
  @ViewChildren(ExperimentListComponent)
  nestedExperimentLists!: QueryList<ExperimentListComponent>;

  @Input() groupsData: any = {};
  @Input() tableColumns: any[] = [];
  @Input() statusOptions: any = [];
  @Input() showFilters: boolean = false;
  @Input() enableCreatedByColumn: boolean = false;
  @Input() accordionTemplate!: TemplateRef<any>;
  @Output() emitTableAction = new EventEmitter<any>();
  @Output() groupExpand = new EventEmitter<{ node: any; level: number }>();

  treeData: any[] = [];
  actionColumnList: any = [];
  currentRoute: string = "";
  overlayDetails: any = {};
  needsExtraStatus = ["cloning", "queued"];
  selectionKeys: Record<string, boolean> = {};
  sortField: string = "created_at";
  sortOrder: number = -1;
  pageSizeOptions: number[] = [10, 20, 50, 100];
  dateFilterApplied: {
    [key: string]: { isDirty: boolean; dateRange: Date[] } | undefined;
  } = {};
  private debounceTimeout: any = null;

  constructor(public stateService: StateService) {}

  ngOnInit(): void {
    this.currentRoute = this.currentRoute =
      this.stateService.getActiveRouteSegment() ?? CatExpertRouterLink.Studio;

    this.selectionKeys = {};
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["showFilters"]?.currentValue === false) {
      this.cancelPendingSearch();
    }
    if (changes["groupsData"]?.currentValue) {
      this.groupsData = changes["groupsData"].currentValue;

      this.treeData = this.groupsData.groups;
    }
  }

  ngOnDestroy(): void {
    this.cancelPendingSearch();
  }

  getGroupLabel(group: any): string {
    return group?.label || group?.key || group?.value || "Untitled";
  }

  isSelectionColumnEnabled(): boolean {
    return (this.tableColumns || []).some(
      (column: any) => column?.value === "check_box" && !column?.disabled,
    );
  }

  getNodePagination(node: any): any {
    const pagination = {
      page: node?.page ?? 1,
      page_size: node?.page_size ?? 20,
      total: node?.total ?? 0,
      total_pages: node?.total_pages ?? 1,
    };
    return {
      page: pagination.page ?? 1,
      page_size: pagination.page_size ?? 20,
      total: pagination.total ?? 0,
      total_pages: pagination.total_pages ?? 1,
    };
  }

  get firstCount(): number {
    const pagination = this.getNodePagination(this.groupsData);
    const total = pagination.total ?? 0;
    if (!total) {
      return 0;
    }

    return (pagination.page - 1) * (pagination.page_size ?? 20) + 1;
  }

  get lastCount(): number {
    const pagination = this.getNodePagination(this.groupsData);
    const total = pagination.total ?? 0;
    if (!total) {
      return 0;
    }

    return Math.min(
      (pagination.page ?? 1) * (pagination.page_size ?? 20),
      total,
    );
  }

  get totalItems(): number {
    return this.groupsData?.total ?? 0;
  }

  getPageRange(node?: any): string {
    const pagination = this.getNodePagination(node ?? this.groupsData);
    const total = pagination.total ?? 0;
    const page = pagination.page ?? 1;
    const pageSize = pagination.page_size ?? 20;
    const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, total);
    return total ? `${start}-${end} of ${total} entries` : `0-0 of 0 entries`;
  }

  getPageSizeSelectValue(
    value: number | string | null | undefined,
    fallback: number = 20,
  ): number {
    const normalizedValue = Number(value);

    if (!Number.isFinite(normalizedValue) || normalizedValue <= 0) {
      return fallback;
    }

    return this.pageSizeOptions.includes(normalizedValue)
      ? normalizedValue
      : fallback;
  }

  changeGroupPage(
    node: any | null,
    nextPage: number,
    nextPageSize?: number,
  ): void {
    const targetNode = node ?? this.groupsData;
    const pagination = this.getNodePagination(targetNode);
    const safePage = Math.min(
      Math.max(nextPage, 1),
      Math.max(pagination.total_pages || 1, 1),
    );
    const pageSize = nextPageSize ?? pagination.page_size ?? 20;

    this.emitTableAction.emit({
      action: { key: "groupPagination", callBack: "groupPaginationAction" },
      data: {
        node: node ?? null,
        page: safePage,
        page_size: pageSize,
        level: node ? (node?.group_by_level ?? 0) + 1 : 1,
      },
    });
  }

  onTableAction(action: any, row: any): void {
    this.emitTableAction.emit({ action, data: row.raw || row });
  }

  private syncSelectionKeysFromNestedLists(): void {
    const nextSelection: Record<string, boolean> = { ...this.selectionKeys };

    this.nestedExperimentLists?.forEach((list: ExperimentListComponent) => {
      (list.tableData || []).forEach((row: any) => {
        const rowKey = this.getNodeSelectionKey(row);
        if (!rowKey) {
          return;
        }

        if (row.isChecked) {
          nextSelection[rowKey] = true;
        } else {
          delete nextSelection[rowKey];
        }
      });
    });

    this.selectionKeys = nextSelection;
    this.synchronizeParentKeysFromDescendantState();
    this.refreshSelectionStateFromKeys();
    this.syncNestedListSelectionState();
  }

  handleNestedExperimentListAction(event: any, node: any): void {
    const callback = event?.action?.callBack;
    if (callback === "paginationAction") {
      node.page = event?.data.page ?? 1;
      node.page_size = event?.data.page_size ?? 20;
      this.emitTableAction.emit({
        action: { key: "groupPagination", callBack: "groupPaginationAction" },
        data: {
          node,
          level: (node?.group_by_level ?? 0) + 1,
        },
      });
      return;
    }

    if (callback === "multipleActions") {
      this.syncSelectionKeysFromNestedLists();
    }

    if (callback === "getPlansForExperiment") {
      this.emitTableAction.emit({
        action: event.action,
        data: event.data,
        parentSelectedItems: event.parentSelectedItems || [],
      });
      return;
    }

    this.emitTableAction.emit({
      action: event.action,
      data: this.getSelectedRowsSnapshot(),
      parentSelectedItems: event.parentSelectedItems || [],
      partialParentSelectedItems: this.getPartiallySelectedParentItems(),
    });
  }

  handleNestedRowCheckboxChange(event: any): void {
    this.syncSelectionKeysFromNestedLists();

    this.emitTableAction.emit({
      action: {
        key: "row-checkbox-change",
        callBack: "experimentRowCheckboxChange",
      },
      data: event,
    });
  }

  onNgSelectChange(selectedItems: any[], column: any) {
    const selectedValues = Array.isArray(selectedItems)
      ? selectedItems.filter((v) => v !== undefined && v !== null)
      : [];

    this.emitTableAction.emit({
      action: {
        key: column.value,
        callBack: "filterAction",
      },
      data: selectedValues,
    });
  }

  filterSearch(value: string, column: any) {
    this.cancelPendingSearch();
    this.debounceTimeout = setTimeout(() => {
      this.debounceTimeout = null;
      this.emitTableAction.emit({
        action: {
          key: column.value,
          callBack: "filterSearchAction",
        },
        data: value ?? "",
      });
    }, 600); // 600ms debounce, adjust as needed
  }

  private cancelPendingSearch(): void {
    if (this.debounceTimeout !== null) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }
  }

  handleClickOutside(event: any, field: string) {
    if (!this.dateFilterApplied[field]?.isDirty) {
      event.value = null;
    } else {
      event.value = this.dateFilterApplied[field]?.dateRange;
    }
  }

  isDatesSelected(dateRange: Date[]) {
    const validDateRange = dateRange?.filter((value: Date) => value != null);
    return !dateRange || (validDateRange && validDateRange.length < 1)
      ? true
      : false;
  }

  resetCalendar(
    field: string,
    calendar?: {
      value?: Date[] | null;
      hideOverlay?: () => void;
      updateInputfield?: () => void;
    },
  ) {
    this.updateDateFilter(field, false, []);
    this.emitTableAction.emit({
      action: {
        key: field,
        callBack: "filterByDate",
      },
      data: [],
    });
    if (calendar) {
      calendar.value = null;
      calendar.updateInputfield?.();
      calendar.hideOverlay?.();
    }
    this.blurActiveElement();
  }

  updateDateFilter(field: string, isDirty: boolean, dateRange: Date[]) {
    this.dateFilterApplied[field] = {
      isDirty,
      dateRange,
    };
  }

  private blurActiveElement(): void {
    const activeElement = document.activeElement as HTMLElement | null;
    activeElement?.blur();
  }

  private normalizeToMidday(date: Date): Date {
    const normalizedDate = new Date(date);
    normalizedDate.setHours(12, 0, 0, 0);
    return normalizedDate;
  }

  private getDateFilterRange(value: Date[]): { start: Date; end: Date } | null {
    if (!value || value.length === 0) {
      return null;
    }

    if (value.length === 1 || !value[1]) {
      const date = value[0];
      return {
        start: this.normalizeToMidday(
          new Date(date.getFullYear(), date.getMonth(), 1),
        ),
        end: this.normalizeToMidday(
          new Date(date.getFullYear(), date.getMonth() + 1, 0),
        ),
      };
    }

    const startDate = value[0];
    const endDate = value[1];
    return {
      start: this.normalizeToMidday(
        new Date(startDate.getFullYear(), startDate.getMonth(), 1),
      ),
      end: this.normalizeToMidday(
        new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0),
      ),
    };
  }

  applyDateFilter(value: Date[], field: string) {
    const dateRange = this.getDateFilterRange(value);
    if (!dateRange) {
      this.updateDateFilter(field, false, []);
      this.emitTableAction.emit({
        action: {
          key: field,
          callBack: "filterByDate",
        },
        data: [],
      });
      this.blurActiveElement();
      return;
    }

    const { start, end } = dateRange;
    this.emitTableAction.emit({
      action: {
        key: field,
        callBack: "filterByDate",
      },
      data: [start, end],
    });
    this.updateDateFilter(field, true, [start, end]);
    this.blurActiveElement();
  }

  experimentAction(option: any) {
    this.emitTableAction.emit({ action: option, data: this.overlayDetails });
  }

  titleAddUnderscore(value: string): string {
    return value ? value.replace(/\s+/g, "_").toLowerCase() : "";
  }

  formatHumanReadableDate(dateStr: string): string {
    // Validate input
    if (!dateStr || typeof dateStr !== "string") {
      return "";
    }

    const inputDateObj = new Date(dateStr + "Z");

    // Check if the date is valid
    if (isNaN(inputDateObj.getTime())) {
      return "";
    }

    const now = new Date();

    const inputDay = new Date(
      inputDateObj.getFullYear(),
      inputDateObj.getMonth(),
      inputDateObj.getDate(),
    );
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const relative =
      inputDay.getTime() === today.getTime()
        ? "today"
        : inputDay.getTime() === yesterday.getTime()
          ? "yesterday"
          : null;
    return formatLocalizedDateTime(inputDateObj, relative);
  }

  isGroupRow(data: any): boolean {
    const raw = data?.raw ?? data;
    const rawData = raw?.data ?? raw;

    if (raw?.leaf === true || rawData?.leaf === true) {
      return false;
    }

    const hasGroupIdentity =
      !!raw?.dimension ||
      !!raw?.children ||
      !!raw?.groups ||
      (typeof raw?.count === "number" && raw.count >= 0) ||
      !!raw?.depth;

    return (
      hasGroupIdentity ||
      (Array.isArray(raw?.children) && raw.children.length > 0) ||
      (Array.isArray(raw?.groups) && raw.groups.length > 0) ||
      (Array.isArray(raw?.items) && raw.items.length > 0 && raw?.children)
    );
  }

  private hasCollapsedNode(node: any): boolean {
    if (!node) {
      return false;
    }

    if (!node.expanded) {
      return true;
    }

    return (node.children || []).some((child: any) =>
      this.hasCollapsedNode(child),
    );
  }

  private hasLoadedChildren(node: any): boolean {
    return !!node?.childrenLoaded;
  }

  hasCollapsedRows(): boolean {
    return this.treeData.some((node: any) => this.hasCollapsedNode(node));
  }

  toggleAllRows(): void {
    const shouldExpand = this.hasCollapsedRows();

    const applyExpandState = (node: any): any => ({
      ...node,
      expanded: shouldExpand,
      children: (node.children || []).map((child: any) =>
        applyExpandState(child),
      ),
    });

    this.treeData = this.treeData.map((node: any) => applyExpandState(node));
  }

  getVisibleRows(): any[] {
    const visibleRows: any[] = [];

    const visitNode = (node: any, level: number = 0) => {
      const rowData = node;
      const isGroup =
        this.isGroupRow(rowData) || (node.children || []).length > 0;

      if (isGroup) {
        visibleRows.push({
          key: node.key,
          type: "group",
          level,
          node,
          data: rowData,
        });

        if (node.expanded) {
          (node.children || []).forEach((child: any) =>
            visitNode(child, level + 1),
          );
        }

        return;
      }

      visibleRows.push({
        key: node.key,
        type: "leaf",
        level,
        node,
        data: rowData,
      });
    };

    this.treeData.forEach((root: any) => visitNode(root, 0));
    return visibleRows;
  }

  getChildGroups(node: any): any[] {
    return (node?.children || []).filter((child: any) =>
      this.isGroupRow(child),
    );
  }

  private dedupeLeafRows(items: any[]): any[] {
    const seen = new Set<string>();

    return items.filter((item: any) => {
      const row = item;
      const key =
        row?.id ??
        item?.id ??
        row?.key ??
        item?.key ??
        `${row?.name ?? item?.name ?? ""}-${row?.created_at ?? item?.created_at ?? ""}`;

      if (!key || seen.has(String(key))) {
        return false;
      }

      seen.add(String(key));
      return true;
    });
  }

  getLeafChildren(node: any): any[] {
    const directExperiments = Array.isArray(node?.experiments)
      ? node.experiments
      : [];

    const nestedLeaves = (node?.children || []).filter(
      (child: any) => !this.isGroupRow(child),
    );

    const mergedLeafRows = [
      ...directExperiments.map((item: any) => ({
        ...item,
        key: item?.id ?? item?.key ?? item?.name,
        data: item,
        leaf: true,
      })),
      ...nestedLeaves.map((child: any) => ({
        ...child,
        key: child?.key ?? child?.id,
        data: child,
        leaf: true,
      })),
    ];

    return this.dedupeLeafRows(mergedLeafRows);
  }

  getLeafItemRows(node: any): any[] {
    return this.getLeafChildren(node).map((child: any) => child.data || child);
  }

  shouldRenderLeafExperimentList(node: any): boolean {
    if (!node) {
      return false;
    }

    if (this.getLeafChildren(node).length) {
      return true;
    }

    return !this.getChildGroups(node).length && this.hasLoadedChildren(node);
  }

  getAllLeafNodes(node: any): any[] {
    if (!node) {
      return [];
    }

    const children = node?.children || [];
    const directExperiments = Array.isArray(node?.data?.experiments)
      ? node.data.experiments
      : Array.isArray(node?.experiments)
        ? node.experiments
        : [];

    if (!children.length && !directExperiments.length) {
      return this.isGroupRow(node) ? [] : [node];
    }

    const leaves: any[] = [];

    if (directExperiments.length) {
      leaves.push(...directExperiments);
    }

    children.forEach((child: any) => {
      const childLeaves = this.getAllLeafNodes(child);
      if (childLeaves.length) {
        leaves.push(...childLeaves);
      }
    });

    return leaves;
  }

  private collectTreeNodes(node: any, nodes: any[] = []): any[] {
    if (!node) {
      return nodes;
    }

    nodes.push(node);
    (node.children || []).forEach((child: any) =>
      this.collectTreeNodes(child, nodes),
    );
    return nodes;
  }

  private getGroupSelectionTargets(node: any): any[] {
    const childGroups = this.getChildGroups(node);
    if (childGroups.length) {
      return childGroups;
    }

    const leaves = this.getAllLeafNodes(node);
    if (leaves.length) {
      return leaves;
    }

    return [];
  }

  isGroupSelected(node: any): boolean {
    return this.getGroupSelectionState(node) === "selected";
  }

  isGroupPartiallySelected(node: any): boolean {
    return this.getGroupSelectionState(node) === "partial";
  }

  private getGroupSelectionState(node: any): "selected" | "partial" | "none" {
    const selectionTargets = this.getGroupSelectionTargets(node);
    if (!selectionTargets.length) {
      const key = this.getNodeSelectionKey(node);
      return key && this.selectionKeys[key] ? "selected" : "none";
    }

    let hasAnySelection = false;
    let allSelected = true;

    selectionTargets.forEach((target: any) => {
      const targetState = this.isGroupRow(target)
        ? this.getGroupSelectionState(target)
        : this.isRowSelected(target)
          ? "selected"
          : "none";

      if (targetState !== "none") {
        hasAnySelection = true;
      }

      if (targetState !== "selected") {
        allSelected = false;
      }
    });

    if (allSelected) {
      return "selected";
    }

    return hasAnySelection ? "partial" : "none";
  }

  private getNodeSelectionKey(node: any): string | null {
    const value = node?.id ?? node?.key ?? null;
    return value === null || value === undefined ? null : String(value);
  }

  private getCheckedParentItems(nodes: any[] = this.treeData): any[] {
    const checkedParents: any[] = [];

    const visit = (items: any[]) => {
      (items || []).forEach((item: any) => {
        if (!item) {
          return;
        }

        if (item.is_parent && item.isChecked) {
          checkedParents.push(item);
        }

        if (Array.isArray(item.children) && item.children.length) {
          visit(item.children);
        }
      });
    };

    visit(nodes);
    return checkedParents;
  }

  private getPartiallySelectedParentItems(): any[] {
    return this.treeData
      .flatMap((node: any) => this.collectTreeNodes(node))
      .filter(
        (node: any) => node.is_parent && this.isGroupPartiallySelected(node),
      );
  }

  private syncNestedListSelection(node: any, checked: boolean): void {
    const selectedLeafKeys = new Set(
      this.getAllLeafNodes(node)
        .map((leaf: any) => this.getNodeSelectionKey(leaf))
        .filter((key): key is string => !!key),
    );

    this.nestedExperimentLists?.forEach((list: ExperimentListComponent) => {
      const rows = list?.tableData || [];

      rows.forEach((row: any) => {
        const rowKey = this.getNodeSelectionKey(row);
        if (!rowKey || !selectedLeafKeys.has(rowKey)) {
          return;
        }

        row.isChecked = checked;

        const selectedIndex = (list.selectedItems || []).findIndex(
          (item: any) => this.getNodeSelectionKey(item) === rowKey,
        );

        if (checked && selectedIndex === -1) {
          list.selectedItems.push(row);
        }

        if (!checked && selectedIndex !== -1) {
          list.selectedItems.splice(selectedIndex, 1);
        }
      });

      if (list.tableData?.length) {
        list.allSelected = list.tableData.every(
          (row: any) =>
            !!row.isChecked ||
            !selectedLeafKeys.has(this.getNodeSelectionKey(row) || ""),
        );
      }
    });
  }

  private applySelectionToTree(
    node: any,
    checked: boolean,
    nextSelection: Record<string, boolean>,
  ): void {
    if (!node) {
      return;
    }

    const nodeKey = this.getNodeSelectionKey(node);
    if (nodeKey) {
      if (checked) {
        nextSelection[nodeKey] = true;
      } else {
        delete nextSelection[nodeKey];
      }
      node.isChecked = checked;
    }

    (node.children || []).forEach((child: any) => {
      this.applySelectionToTree(child, checked, nextSelection);
    });

    this.getAllLeafNodes(node).forEach((leaf: any) => {
      const leafKey = this.getNodeSelectionKey(leaf);
      if (!leafKey) {
        return;
      }

      if (checked) {
        nextSelection[leafKey] = true;
      } else {
        delete nextSelection[leafKey];
      }

      if (leaf) {
        leaf.isChecked = checked;
      }
    });
  }

  private refreshSelectionStateFromKeys(): void {
    const visitNode = (node: any) => {
      if (!node) {
        return;
      }

      const nodeKey = this.getNodeSelectionKey(node);
      const selectionTargets = this.getGroupSelectionTargets(node);

      if (nodeKey) {
        const isChecked = selectionTargets.length
          ? selectionTargets.every((target: any) => this.isRowSelected(target))
          : !!this.selectionKeys[nodeKey];
        node.isChecked = isChecked;
      }

      (node.children || []).forEach((child: any) => visitNode(child));
    };

    this.treeData.forEach((root: any) => visitNode(root));
  }

  private syncNestedListSelectionState(): void {
    this.nestedExperimentLists?.forEach((list: ExperimentListComponent) => {
      const rows = list?.tableData || [];

      rows.forEach((row: any) => {
        const rowKey = this.getNodeSelectionKey(row);
        row.isChecked = !!(rowKey && this.selectionKeys[rowKey]);
      });

      list.selectedItems = rows.filter((row: any) => {
        const rowKey = this.getNodeSelectionKey(row);
        return !!(rowKey && this.selectionKeys[rowKey]);
      });

      list.allSelected =
        rows.length > 0 &&
        rows.every((row: any) => {
          const rowKey = this.getNodeSelectionKey(row);
          return !rowKey || !!this.selectionKeys[rowKey];
        });
    });
  }

  private synchronizeParentKeysFromDescendantState(): void {
    const nextSelection: Record<string, boolean> = { ...this.selectionKeys };

    const checkNode = (node: any) => {
      if (!node) {
        return;
      }

      (node.children || []).forEach((child: any) => checkNode(child));

      const nodeKey = this.getNodeSelectionKey(node);
      const selectionTargets = this.getGroupSelectionTargets(node);

      if (!nodeKey || !selectionTargets.length) {
        return;
      }

      const allDescendantsSelected = selectionTargets.every((target: any) => {
        const targetKey = this.getNodeSelectionKey(target);
        return !!(targetKey && nextSelection[targetKey]);
      });

      if (allDescendantsSelected) {
        nextSelection[nodeKey] = true;
        return;
      }

      delete nextSelection[nodeKey];
    };

    this.treeData.forEach((root: any) => checkNode(root));
    this.selectionKeys = nextSelection;
  }

  private getSelectedRowsSnapshot(): any[] {
    const selectedRows: any[] = [];
    const seen = new Set<string>();

    const visitNode = (node: any) => {
      if (!node) {
        return;
      }

      const key = this.getNodeSelectionKey(node);
      const leafNodes = this.getAllLeafNodes(node);

      if (leafNodes.length) {
        leafNodes.forEach((leaf: any) => {
          const leafKey = this.getNodeSelectionKey(leaf);
          if (!leafKey || !this.selectionKeys[leafKey] || seen.has(leafKey)) {
            return;
          }

          seen.add(leafKey);
          selectedRows.push(leaf);
        });
      }

      // Only include parent if it's in selectionKeys AND all its descendants are selected
      if (key && this.selectionKeys[key] && !seen.has(key)) {
        const selectionTargets = this.getGroupSelectionTargets(node);
        const isFullySelected =
          selectionTargets.length === 0 ||
          selectionTargets.every((target: any) => this.isRowSelected(target));

        if (isFullySelected) {
          seen.add(key);
          selectedRows.push(node);
        }
      }

      (node.children || []).forEach((child: any) => visitNode(child));
    };

    this.treeData.forEach((root: any) => visitNode(root));

    return selectedRows;
  }

  private emitSelectionSnapshot(): void {
    this.emitTableAction.emit({
      action: {
        key: "multipleActions",
        callBack: "multipleActions",
      },
      data: this.getSelectedRowsSnapshot(),
      parentSelectedItems: this.getCheckedParentItems(),
      partialParentSelectedItems: this.getPartiallySelectedParentItems(),
    });
  }

  toggleGroupSelection(node: any, checked: boolean): void {
    const nextSelection: Record<string, boolean> = { ...this.selectionKeys };
    this.applySelectionToTree(node, checked, nextSelection);
    this.selectionKeys = nextSelection;
    this.refreshSelectionStateFromKeys();
    this.syncNestedListSelection(node, checked);
    this.syncNestedListSelectionState();
    this.emitSelectionSnapshot();
  }

  isNodeExpanded(node: any): boolean {
    return !!node?.expanded;
  }

  toggleNodeExpand(node: any): void {
    if (!node) {
      return;
    }

    const shouldExpand = !node.expanded;
    node.expanded = shouldExpand;

    const hasChildren = (node.children || []).length > 0;
    if (shouldExpand && !hasChildren && !this.hasLoadedChildren(node)) {
      const currentDepth = node?.group_by_level ?? 1;
      this.groupExpand.emit({
        node: node,
        level: currentDepth + 1,
      });
    }
  }

  toggleRowExpand(node: any): void {
    if (!node) {
      return;
    }

    node.expanded = !node.expanded;
  }

  trackRowKey(index: number, row: any): string {
    return row?.key || `${index}`;
  }

  isRowSelected(node: any): boolean {
    const source = node;
    const key = this.getNodeSelectionKey(source);
    return !!(key && this.selectionKeys[key]);
  }

  toggleRowSelection(node: any, checked: boolean): void {
    const source = node;
    const key = this.getNodeSelectionKey(source);
    if (!key) {
      return;
    }

    const nextSelection: Record<string, boolean> = { ...this.selectionKeys };

    if (checked) {
      nextSelection[key] = true;
    } else {
      delete nextSelection[key];
    }

    source.isChecked = checked;
    this.selectionKeys = nextSelection;

    this.synchronizeParentKeysFromDescendantState();

    this.refreshSelectionStateFromKeys();
    this.emitSelectionSnapshot();
  }

  isAllRowsSelected(): boolean {
    const rows = this.getVisibleRows();
    if (!rows.length) {
      return false;
    }

    return rows.every((row: any) => this.isRowSelected(row.node ?? row));
  }

  isSomeRowsSelected(): boolean {
    const rows = this.getVisibleRows();
    if (!rows.length) {
      return false;
    }

    const selectedCount = rows.filter((row: any) =>
      this.isRowSelected(row.node ?? row),
    ).length;

    return selectedCount > 0 && selectedCount < rows.length;
  }

  toggleAllSelection(checked: boolean): void {
    const nextSelection: Record<string, boolean> = { ...this.selectionKeys };
    const visibleRows = this.getVisibleRows();

    this.treeData.forEach((root: any) => {
      this.applySelectionToTree(root, checked, nextSelection);
    });
    this.selectionKeys = nextSelection;
    this.refreshSelectionStateFromKeys();

    visibleRows.forEach((row: any) => {
      const source = row.node ?? row;
      const rowKey = this.getNodeSelectionKey(source);
      if (!rowKey) {
        return;
      }
      if (source) {
        source.isChecked = checked;
      }
    });

    this.nestedExperimentLists?.forEach((list: ExperimentListComponent) => {
      const rows = list?.tableData || [];
      rows.forEach((row: any) => {
        const rowKey = this.getNodeSelectionKey(row);
        const shouldBeSelected = visibleRows.some((visible: any) => {
          const visibleKey = this.getNodeSelectionKey(
            visible.node ?? visible.data ?? visible,
          );
          return !!(visibleKey && rowKey && visibleKey === rowKey);
        });

        row.isChecked = checked && shouldBeSelected;

        const selectedIndex = (list.selectedItems || []).findIndex(
          (item: any) => this.getNodeSelectionKey(item) === rowKey,
        );

        if (checked && shouldBeSelected && selectedIndex === -1) {
          list.selectedItems.push(row);
        }

        if ((!checked || !shouldBeSelected) && selectedIndex !== -1) {
          list.selectedItems.splice(selectedIndex, 1);
        }
      });

      if (list.tableData?.length) {
        list.allSelected = list.tableData.every(
          (row: any) => !this.getNodeSelectionKey(row) || !!row.isChecked,
        );
      }
    });

    this.syncNestedListSelectionState();
    this.emitSelectionSnapshot();
  }

  toggleSort(column: any): void {
    if (!column?.value) {
      return;
    }

    if (this.sortField === column.value) {
      this.sortOrder = this.sortOrder === 1 ? -1 : 1;
    } else {
      this.sortField = column.value;
      this.sortOrder = -1;
    }

    this.emitTableAction.emit({
      action: {
        key: "sort",
        callBack: "sortingAction",
      },
      data: {
        sort: this.sortField
          ? [
              {
                field: this.sortField,
                direction: this.sortOrder === 1 ? "asc" : "desc",
              },
            ]
          : [],
        page: this.groupsData?.page ?? 1,
        page_size: this.groupsData?.page_size ?? 20,
      },
    });
  }

  isSortedColumn(column: any): boolean {
    return this.sortField === column.value;
  }

  isAscending(column: any): boolean {
    return this.isSortedColumn(column) && this.sortOrder === 1;
  }

  isDescending(column: any): boolean {
    return this.isSortedColumn(column) && this.sortOrder === -1;
  }
}
